/* Global red banner shown on EVERY page when the current session is an
   admin impersonation. Reads `user.impersonation` from AuthContext.
   Sits at the very top above all other UI, sticky, with a one-click
   "Esci dall'impersonation" button that restores the admin session
   stored in localStorage by VIPAdminUserDetail before impersonating.
*/
import React, { useState } from "react";
import { ShieldAlert, LogOut } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const IMPERSONATION_BACKUP_KEY = "mqit_admin_session_backup_v1";

export function stashAdminSessionForImpersonation() {
  /* Called by VIPAdminUserDetail BEFORE invoking the impersonate endpoint.
     We snapshot the current `session_token` cookie value so we can put
     it back when the admin clicks "Stop impersonating". */
  try {
    const cookie = (document.cookie || "")
      .split(";").map((c) => c.trim())
      .find((c) => c.startsWith("session_token="));
    if (cookie) {
      const val = cookie.substring("session_token=".length);
      window.localStorage.setItem(IMPERSONATION_BACKUP_KEY, val);
    }
  } catch { /* ignore */ }
}

function restoreAdminSession() {
  /* The /stop endpoint already deleted the impersonation cookie. We
     re-issue the admin's original cookie so the next request goes back
     to the admin's identity without forcing a re-login. */
  try {
    const adminToken = window.localStorage.getItem(IMPERSONATION_BACKUP_KEY);
    if (adminToken) {
      const expires = new Date(Date.now() + 1000 * 3600 * 24 * 7).toUTCString();
      document.cookie = `session_token=${adminToken}; expires=${expires}; path=/; SameSite=Lax`;
      window.localStorage.removeItem(IMPERSONATION_BACKUP_KEY);
      return true;
    }
  } catch { /* ignore */ }
  return false;
}

export default function ImpersonationBanner() {
  const { user, refresh } = useAuth();
  const [stopping, setStopping] = useState(false);

  if (!user || !user.impersonation) return null;

  const stop = async () => {
    if (stopping) return;
    setStopping(true);
    try {
      await api.post("/admin/vip/impersonation/stop");
      restoreAdminSession();
      await refresh();
      // Hard reload so any in-memory pages re-fetch with admin perms.
      window.location.href = "/admin/vip";
    } catch (e) {
      // If something fails, force a clean logout flow.
      window.location.href = "/admin-login";
    } finally {
      setStopping(false);
    }
  };

  const adminEmail = user.impersonation.admin_email || "admin";
  const expiresAt = user.impersonation.expires_at;
  let expiryLabel = "";
  try {
    if (expiresAt) {
      const d = new Date(expiresAt);
      expiryLabel = d.toLocaleTimeString("it-IT", {
        hour: "2-digit", minute: "2-digit",
      });
    }
  } catch { /* ignore */ }

  return (
    <div data-testid="impersonation-banner"
         className="sticky top-0 z-[100] w-full"
         style={{
           background: "linear-gradient(90deg,#9E1B32 0%,#C8102E 50%,#9E1B32 100%)",
           color: "white",
           borderBottom: "2px solid #6B0F22",
           boxShadow: "0 4px 14px rgba(158,27,50,0.4)",
         }}>
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex flex-wrap items-center gap-3">
        <ShieldAlert className="h-5 w-5 shrink-0 animate-pulse" />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm uppercase tracking-wider">
            STAI IMPERSONANDO · {user.email || user.name || user.user_id}
          </p>
          <p className="text-xs opacity-90">
            Admin: <span className="font-mono">{adminEmail}</span>
            {expiryLabel ? ` · scade alle ${expiryLabel}` : ""}
          </p>
        </div>
        <button
          onClick={stop}
          disabled={stopping}
          data-testid="impersonation-stop-btn"
          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-white text-rose-700 text-sm font-semibold hover:bg-rose-50 transition-colors disabled:opacity-60">
          <LogOut className="h-4 w-4" />
          {stopping ? "Uscita…" : "Esci dall'impersonation"}
        </button>
      </div>
    </div>
  );
}
